import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Shield, LogOut, AlertCircle, CheckCircle, Trash2, Download, RefreshCw, Calendar, Users, BookOpen, Plus, Edit, Save, X, Clock, MapPin, Search, Settings, Key, Database, Bell } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-provider';
import { useStoreActions } from '@/store/useStore';
import { DataProcessor } from '@/utils/dataProcessor';

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { initializeData } = useStoreActions();
    const [activeTab, setActiveTab] = useState<'overview' | 'classes' | 'cancelled' | 'teachers' | 'notifications' | 'settings'>('overview');
    const [stats, setStats] = useState({
        totalPrograms: 0,
        totalClasses: 0,
        totalTeachers: 0,
        cancelledClasses: 0,
    });
    const [cancelledClasses, setCancelledClasses] = useState<any[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [allClasses, setAllClasses] = useState<any[]>([]);
    const [filteredClasses, setFilteredClasses] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDay, setFilterDay] = useState('');
    const [editingClass, setEditingClass] = useState<any>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [teachers, setTeachers] = useState<string[]>([]);
    
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('adminToken');
            const expiry = localStorage.getItem('adminTokenExpiry');

            if (!token || !expiry) {
                navigate('/admin/login');
                return false;
            }

            if (Date.now() > parseInt(expiry)) {
                handleLogout();
                return false;
            }

            return true;
        };

        if (checkAuth()) {
            loadData();
        }

        const interval = setInterval(checkAuth, 60000);
        return () => clearInterval(interval);
    }, [navigate]);

    const loadData = async () => {
        await initializeData();
        const dataProcessor = DataProcessor.getInstance();
        const programs = dataProcessor.getAllPrograms();
        const teachersList = dataProcessor.getTeachers();

        // Load all classes
        const classes: any[] = [];
        let totalClasses = 0;
        programs.forEach(program => {
            Object.entries(program.schedule).forEach(([day, courses]: [string, any]) => {
                courses.forEach((course: any, index: number) => {
                    const classData = {
                        id: `${program.id}-${day}-${index}`,
                        day,
                        time: course.time,
                        course: course.course_name || course.course,
                        room: course.location,
                        instructor: course.instructor || course.teacher,
                        program: program.program,
                        semester: program.semester,
                        group: program.group,
                        department: program.department,
                        programId: program.id,
                    };
                    classes.push(classData);
                    totalClasses++;
                });
            });
        });

        const cancelled = JSON.parse(localStorage.getItem('cancelledClasses') || '[]');
        const notifications = JSON.parse(localStorage.getItem('studentNotifications') || '[]');
        
        const cancelledData = notifications
            .filter((n: any) => n.type === 'class-cancelled')
            .map((n: any) => ({
                classId: n.classId,
                course: n.course,
                instructor: n.instructor,
                day: n.day,
                time: n.time,
                room: n.location,
                program: n.program,
                semester: n.semester,
                group: n.group,
                cancelledAt: n.timestamp,
            }));

        setStats({
            totalPrograms: programs.length,
            totalClasses,
            totalTeachers: teachersList.length,
            cancelledClasses: cancelled.length,
        });

        setAllClasses(classes);
        setFilteredClasses(classes);
        setCancelledClasses(cancelledData);
        setTeachers(teachersList);
    };

    // Filter classes
    useEffect(() => {
        let filtered = [...allClasses];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(c =>
                c.course.toLowerCase().includes(query) ||
                c.instructor.toLowerCase().includes(query) ||
                c.room.toLowerCase().includes(query) ||
                c.program.toLowerCase().includes(query)
            );
        }

        if (filterDay) {
            filtered = filtered.filter(c => c.day === filterDay);
        }

        setFilteredClasses(filtered);
    }, [allClasses, searchQuery, filterDay]);

    const handleLogout = () => {
        const loginLog = JSON.parse(localStorage.getItem('adminLoginLog') || '[]');
        loginLog.push({
            timestamp: new Date().toISOString(),
            status: 'logout',
            username: localStorage.getItem('adminUsername')
        });
        localStorage.setItem('adminLoginLog', JSON.stringify(loginLog.slice(-50)));

        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminTokenExpiry');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
    };

    const handleRestoreClass = (classId: string) => {
        if (confirm('Are you sure you want to restore this class?')) {
            const cancelled = JSON.parse(localStorage.getItem('cancelledClasses') || '[]');
            const updated = cancelled.filter((id: string) => id !== classId);
            localStorage.setItem('cancelledClasses', JSON.stringify(updated));

            const notifs = JSON.parse(localStorage.getItem('studentNotifications') || '[]');
            const classInfo = cancelledClasses.find(c => c.classId === classId);
            if (classInfo) {
                notifs.push({
                    id: `notif-${Date.now()}`,
                    classId: classId,
                    type: 'class-resumed',
                    title: 'Class Resumed',
                    message: `${classInfo.course} on ${classInfo.day} at ${classInfo.time} has been resumed by admin`,
                    course: classInfo.course,
                    instructor: classInfo.instructor,
                    location: classInfo.room,
                    time: classInfo.time,
                    day: classInfo.day,
                    program: classInfo.program,
                    semester: classInfo.semester,
                    group: classInfo.group,
                    timestamp: new Date().toISOString(),
                    priority: 'medium'
                });
                localStorage.setItem('studentNotifications', JSON.stringify(notifs));
            }

            loadData();
            setSuccessMessage('Class restored successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleClearAll = () => {
        if (confirm('Are you sure you want to clear all cancelled classes? This action cannot be undone.')) {
            localStorage.setItem('cancelledClasses', JSON.stringify([]));
            localStorage.setItem('studentNotifications', JSON.stringify([]));
            loadData();
            setSuccessMessage('All cancelled classes cleared!');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleEditClass = (classItem: any) => {
        setEditingClass({ ...classItem });
        setIsAddingNew(false);
    };

    const handleAddNew = () => {
        setEditingClass({
            id: '',
            day: 'Monday',
            time: '08:00 AM-09:30 AM',
            course: '',
            room: '',
            instructor: '',
            program: '',
            semester: '',
            group: '',
            department: '',
        });
        setIsAddingNew(true);
    };

    const handleSaveClass = () => {
        if (!editingClass) return;

        if (isAddingNew) {
            setSuccessMessage('Note: Adding new classes requires backend integration. This is a demo.');
        } else {
            setSuccessMessage('Note: Editing classes requires backend integration. This is a demo.');
        }

        setEditingClass(null);
        setIsAddingNew(false);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    const handleDeleteClass = (_classId: string) => {
        if (confirm('Are you sure you want to delete this class?')) {
            setSuccessMessage('Note: Deleting classes requires backend integration. This is a demo.');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleExportData = () => {
        const data = {
            classes: JSON.parse(localStorage.getItem('cancelledClasses') || '[]'),
            notifications: JSON.parse(localStorage.getItem('studentNotifications') || '[]'),
            loginLog: JSON.parse(localStorage.getItem('adminLoginLog') || '[]'),
            exportDate: new Date().toISOString(),
        };
        
        const dataStr = JSON.stringify(data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        
        const exportFileName = `admin_data_export_${new Date().toISOString().split('T')[0]}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileName);
        linkElement.click();

        setSuccessMessage('Data exported successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur-md">
                <div className="flex items-center justify-between h-16 px-4 max-w-7xl mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                            <Shield className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold">Admin Portal</p>
                            <p className="text-xs text-muted-foreground">System Management</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <Button variant="ghost" size="sm" onClick={handleLogout}>
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <nav className="border-b bg-card">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex gap-1 overflow-x-auto py-2">
                        <Button
                            variant={activeTab === 'overview' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('overview')}
                        >
                            <Calendar className="w-4 h-4 mr-2" />
                            Overview
                        </Button>
                        <Button
                            variant={activeTab === 'classes' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('classes')}
                        >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Classes
                        </Button>
                        <Button
                            variant={activeTab === 'cancelled' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('cancelled')}
                        >
                            <AlertCircle className="w-4 h-4 mr-2" />
                            Cancelled
                        </Button>
                        <Button
                            variant={activeTab === 'teachers' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('teachers')}
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Teachers
                        </Button>
                        <Button
                            variant={activeTab === 'notifications' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('notifications')}
                        >
                            <Bell className="w-4 h-4 mr-2" />
                            Notifications
                        </Button>
                        <Button
                            variant={activeTab === 'settings' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setActiveTab('settings')}
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 py-6">
                {successMessage && (
                    <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{successMessage}</span>
                    </div>
                )}

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        <div>
                            <h1 className="text-2xl font-bold mb-2">System Overview</h1>
                            <p className="text-muted-foreground">Real-time statistics and system status</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-1">Total Programs</p>
                                        <p className="text-3xl font-bold">{stats.totalPrograms}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                                        <Calendar className="w-6 h-6 text-blue-500" />
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-1">Total Classes</p>
                                        <p className="text-3xl font-bold">{stats.totalClasses}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-between">
                                        <BookOpen className="w-6 h-6 text-green-500" />
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-1">Teachers</p>
                                        <p className="text-3xl font-bold">{stats.totalTeachers}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                                        <Users className="w-6 h-6 text-purple-500" />
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-1">Cancelled Classes</p>
                                        <p className="text-3xl font-bold">{stats.cancelledClasses}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                                        <AlertCircle className="w-6 h-6 text-red-500" />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Cancelled Classes Tab */}
                {activeTab === 'cancelled' && (
                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold mb-2">Cancelled Classes</h1>
                                <p className="text-muted-foreground">{cancelledClasses.length} cancelled classes</p>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={loadData}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                                {cancelledClasses.length > 0 && (
                                    <Button variant="destructive" onClick={handleClearAll}>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Clear All
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-3">
                            {cancelledClasses.length === 0 ? (
                                <Card className="p-12 text-center">
                                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                                    <p className="text-muted-foreground">No cancelled classes</p>
                                </Card>
                            ) : (
                                cancelledClasses.map((classItem) => (
                                    <div
                                        key={classItem.classId}
                                        className="border-l-4 border-l-red-500 bg-card rounded-lg p-4 hover-elevate transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle className="w-5 h-5 text-red-500" />
                                                    <h3 className="font-semibold text-lg">{classItem.course}</h3>
                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-600 font-medium">
                                                        Cancelled
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                                                    <p>📅 {classItem.day} • {classItem.time}</p>
                                                    <p>📍 {classItem.room}</p>
                                                    <p>👨‍🏫 {classItem.instructor}</p>
                                                    <p>🎓 {classItem.program} • Sem {classItem.semester} • Group {classItem.group}</p>
                                                    <p className="col-span-2 text-xs">
                                                        Cancelled: {new Date(classItem.cancelledAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleRestoreClass(classItem.classId)}
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Restore
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Classes Management Tab */}
                {activeTab === 'classes' && (
                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold mb-2">Class Management</h1>
                                <p className="text-muted-foreground">{filteredClasses.length} of {allClasses.length} classes</p>
                            </div>
                            <Button onClick={handleAddNew}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add New Class
                            </Button>
                        </div>

                        {/* Filters */}
                        <Card className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search classes..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                                <select
                                    value={filterDay}
                                    onChange={(e) => setFilterDay(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">All Days</option>
                                    {daysOfWeek.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>
                        </Card>

                        {/* Edit/Add Form */}
                        {editingClass && (
                            <Card className="p-6 border-2 border-primary">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold">
                                        {isAddingNew ? 'Add New Class' : 'Edit Class'}
                                    </h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setEditingClass(null);
                                            setIsAddingNew(false);
                                        }}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium">Course Name</label>
                                        <Input
                                            value={editingClass.course}
                                            onChange={(e) => setEditingClass({ ...editingClass, course: e.target.value })}
                                            placeholder="e.g., Database Systems"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Instructor</label>
                                        <Input
                                            value={editingClass.instructor}
                                            onChange={(e) => setEditingClass({ ...editingClass, instructor: e.target.value })}
                                            placeholder="e.g., Dr. Smith"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Day</label>
                                        <select
                                            value={editingClass.day}
                                            onChange={(e) => setEditingClass({ ...editingClass, day: e.target.value })}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                            {daysOfWeek.map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Time</label>
                                        <Input
                                            value={editingClass.time}
                                            onChange={(e) => setEditingClass({ ...editingClass, time: e.target.value })}
                                            placeholder="e.g., 08:00 AM-09:30 AM"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Room</label>
                                        <Input
                                            value={editingClass.room}
                                            onChange={(e) => setEditingClass({ ...editingClass, room: e.target.value })}
                                            placeholder="e.g., LR-101"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Program</label>
                                        <Input
                                            value={editingClass.program}
                                            onChange={(e) => setEditingClass({ ...editingClass, program: e.target.value })}
                                            placeholder="e.g., BS Computer Science"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-6">
                                    <Button onClick={handleSaveClass}>
                                        <Save className="w-4 h-4 mr-2" />
                                        Save Changes
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setEditingClass(null);
                                            setIsAddingNew(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Card>
                        )}

                        {/* Classes List */}
                        <div className="space-y-3">
                            {filteredClasses.length === 0 ? (
                                <Card className="p-12 text-center">
                                    <p className="text-muted-foreground">No classes found</p>
                                </Card>
                            ) : (
                                filteredClasses.slice(0, 50).map((classItem) => (
                                    <div
                                        key={classItem.id}
                                        className="border-l-4 border-l-primary bg-card rounded-lg p-4 hover-elevate transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                                                <div className="font-mono text-sm font-medium text-primary flex items-center gap-2">
                                                    <Clock className="w-4 h-4" />
                                                    {classItem.time}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-base">{classItem.course}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {classItem.program} • Sem {classItem.semester}
                                                    </p>
                                                </div>
                                                <div className="text-sm flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                                    {classItem.room}
                                                </div>
                                                <div className="text-sm flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-muted-foreground" />
                                                    {classItem.instructor}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 flex-shrink-0">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleEditClass(classItem)}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => handleDeleteClass(classItem.id)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                            {filteredClasses.length > 50 && (
                                <p className="text-center text-sm text-muted-foreground">
                                    Showing first 50 of {filteredClasses.length} classes
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Teachers Tab */}
                {activeTab === 'teachers' && (
                    <div className="space-y-6">
                        <div>
                            <h1 className="text-2xl font-bold mb-2">Teacher Management</h1>
                            <p className="text-muted-foreground">{teachers.length} teachers</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {teachers.map((teacher, index) => {
                                const teacherClasses = allClasses.filter(c => 
                                    c.instructor.toLowerCase().includes(teacher.toLowerCase())
                                );
                                return (
                                    <Card key={index} className="p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <Users className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold truncate">{teacher}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {teacherClasses.length} classes
                                                </p>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Notifications Tab - Removed */}
                {activeTab === 'notifications' && (
                  <div className="neu-card rounded-2xl p-6 text-center">
                    <p className="text-muted-foreground">Notification management has been removed.</p>
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="space-y-6">
                        <div>
                            <h1 className="text-2xl font-bold mb-2">Admin Settings</h1>
                            <p className="text-muted-foreground">Manage admin portal settings and security</p>
                        </div>

                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Key className="w-6 h-6 text-primary" />
                                <h2 className="text-xl font-semibold">Security Settings</h2>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">Session Timeout</p>
                                        <p className="text-sm text-muted-foreground">30 minutes</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-4 border-t">
                                    <div>
                                        <p className="font-medium">Account Lockout</p>
                                        <p className="text-sm text-muted-foreground">3 failed attempts = 15 min lock</p>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Database className="w-6 h-6 text-primary" />
                                <h2 className="text-xl font-semibold">Data Management</h2>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">Export System Data</p>
                                        <p className="text-sm text-muted-foreground">
                                            Download all cancelled classes, notifications, and logs
                                        </p>
                                    </div>
                                    <Button onClick={handleExportData}>
                                        <Download className="w-4 h-4 mr-2" />
                                        Export
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}


            </main>
        </div>
    );
}
